import React, { useEffect, useState } from 'react';
import { 
  CalendarIcon, 
  CurrencyDollarIcon, 
  TruckIcon, 
  UsersIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import apiService from '../../services/api';
import { DateDisplay } from '../../components/Forms';
import AdminDashboard from './AdminDashboard';

interface DashboardStats {
  today: { bookings: number; revenue: number };
  month: { bookings: number; revenue: number };
  pending: { bookings: number };
  active: { trips: number };
}


const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState<DashboardStats>({
    today: { bookings: 0, revenue: 0 },
    month: { bookings: 0, revenue: 0 },
    pending: { bookings: 0 },
    active: { trips: 0 }
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Don't call dashboard stats API for super admin users (system admins)
      // Only call it for vendor users who have a currentVendor context
      const currentVendor = JSON.parse(localStorage.getItem('currentVendor') || '{}');
      if (user?.role === 'super_admin' || !currentVendor.vendorId) {
        console.log('🚫 Dashboard: Skipping API call for system admin or user without vendor context');
        setStats({
          today: { bookings: 0, revenue: 0 },
          month: { bookings: 0, revenue: 0 },
          pending: { bookings: 0 },
          active: { trips: 0 }
        });
        return;
      }
      
      const dashboardStats = await apiService.getDashboardStats();
      setStats(dashboardStats);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      setError('فشل في تحميل بيانات لوحة التحكم');
    } finally {
      setIsLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'SAR',
      minimumFractionDigits: 0
    }).format(amount).replace('SAR', 'ر.س');
  };

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('en-US').format(num);
  };

  const StatCard: React.FC<{
    title: string;
    value: string | number;
    icon: React.ElementType;
    change?: number;
    changeType?: 'increase' | 'decrease';
    color: string;
    isLoading?: boolean;
  }> = ({ title, value, icon: Icon, change, changeType, color, isLoading }) => (
    <div className="card">
      <div className="flex items-center">
        <div className="flex-shrink-0">
          <div className={`w-12 h-12 rounded-lg ${color} flex items-center justify-center`}>
            <Icon className="w-6 h-6 text-white" />
          </div>
        </div>
        <div className="ml-4 flex-1">
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className="text-2xl font-bold text-gray-900">
            {isLoading ? (
              <div className="animate-pulse bg-gray-200 h-8 w-20 rounded"></div>
            ) : (
              value
            )}
          </p>
          {change !== undefined && !isLoading && (
            <div className={`flex items-center text-sm ${
              changeType === 'increase' ? 'text-green-600' : 'text-red-600'
            }`}>
              {changeType === 'increase' ? (
                <ArrowUpIcon className="w-4 h-4 mr-1" />
              ) : (
                <ArrowDownIcon className="w-4 h-4 mr-1" />
              )}
              {change}%
            </div>
          )}
        </div>
      </div>
    </div>
  );

  // Render admin dashboard if user is system admin (not vendor admin)
  const currentVendor = JSON.parse(localStorage.getItem('currentVendor') || '{}');
  if (user?.role === 'super_admin' && !currentVendor.vendorId) {
    return <AdminDashboard />;
  }

  return (
    <div className="space-y-6 rtl">
      {/* Welcome Section */}
      <div className="bg-gradient-to-l from-blue-50 to-blue-100 rounded-xl p-6 border border-blue-200">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold mb-2 text-gray-900">
              أهلاً وسهلاً، {user?.fullName}
            </h1>
            <p className="text-gray-700">
              مرحباً بك في نظام إدارة الحج والعمرة - {user?.branch?.name}
              {currentVendor.vendorName && ` - ${currentVendor.vendorName}`}
            </p>
            <div className="mt-4 text-sm text-gray-600">
              اليوم: <DateDisplay value={new Date()} className="inline-flex" />
            </div>
          </div>
          <button
            onClick={loadDashboardData}
            disabled={isLoading}
            className="p-2 rounded-lg bg-primary-600 hover:bg-primary-700 transition-colors disabled:opacity-50 text-white"
          >
            <ArrowPathIcon className={`w-5 h-5 text-white ${isLoading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex">
            <div className="ml-3">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div>
              <h3 className="text-sm font-medium text-red-800">خطأ في تحميل البيانات</h3>
              <div className="mt-2 text-sm text-red-700">
                <p>{error}</p>
              </div>
              <div className="mt-4">
                <button
                  type="button"
                  className="bg-red-100 px-3 py-2 rounded-md text-sm font-medium text-red-800 hover:bg-red-200"
                  onClick={loadDashboardData}
                >
                  إعادة المحاولة
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Statistics Grid */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="حجوزات اليوم"
          value={formatNumber(stats.today.bookings)}
          icon={CalendarIcon}
          color="bg-blue-500"
          isLoading={isLoading}
        />
        <StatCard
          title="إيرادات اليوم"
          value={formatCurrency(stats.today.revenue)}
          icon={CurrencyDollarIcon}
          color="bg-green-500"
          isLoading={isLoading}
        />
        <StatCard
          title="حجوزات الشهر"
          value={formatNumber(stats.month.bookings)}
          icon={UsersIcon}
          color="bg-gold-500"
          isLoading={isLoading}
        />
        <StatCard
          title="الحجوزات المعلقة"
          value={formatNumber(stats.pending.bookings)}
          icon={CalendarIcon}
          color="bg-orange-500"
          isLoading={isLoading}
        />
      </div>

      {/* Secondary Stats */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        <StatCard
          title="الرحلات النشطة"
          value={formatNumber(stats.active.trips)}
          icon={TruckIcon}
          color="bg-purple-500"
          isLoading={isLoading}
        />
        <StatCard
          title="إيرادات الشهر"
          value={formatCurrency(stats.month.revenue)}
          icon={CurrencyDollarIcon}
          color="bg-indigo-500"
          isLoading={isLoading}
        />
        <StatCard
          title="إجمالي الحجوزات"
          value={formatNumber(stats.today.bookings + stats.month.bookings)}
          icon={UsersIcon}
          color="bg-cyan-500"
          isLoading={isLoading}
        />
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-1">
        {/* Recent Activity Summary */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">ملخص النشاط</h3>
          </div>
          <div className="space-y-4">
            <div className="flex items-center justify-between py-3 border-b border-gray-100">
              <div className="flex items-center flex-1">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                  <CalendarIcon className="w-4 h-4 text-blue-600" />
                </div>
                <div className="flex-1 pr-2">
                  <p className="font-medium text-gray-900 text-right">حجوزات اليوم</p>
                  <p className="text-sm text-gray-500 text-right">إجمالي الحجوزات المسجلة اليوم</p>
                </div>
              </div>
              <span className="text-lg font-bold text-blue-600">
                {formatNumber(stats.today.bookings)}
              </span>
            </div>
            
            <div className="flex items-center justify-between py-3 border-b border-gray-100">
              <div className="flex items-center flex-1">
                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center mr-3">
                  <CurrencyDollarIcon className="w-4 h-4 text-green-600" />
                </div>
                <div className="flex-1 pr-2">
                  <p className="font-medium text-gray-900 text-right">إيرادات اليوم</p>
                  <p className="text-sm text-gray-500 text-right">إجمالي الإيرادات المحققة اليوم</p>
                </div>
              </div>
              <span className="text-lg font-bold text-green-600">
                {formatCurrency(stats.today.revenue)}
              </span>
            </div>

            <div className="flex items-center justify-between py-3">
              <div className="flex items-center flex-1">
                <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center mr-3">
                  <UsersIcon className="w-4 h-4 text-orange-600" />
                </div>
                <div className="flex-1 pr-2">
                  <p className="font-medium text-gray-900 text-right">حجوزات معلقة</p>
                  <p className="text-sm text-gray-500 text-right">حجوزات تحتاج للمراجعة</p>
                </div>
              </div>
              <span className="text-lg font-bold text-orange-600">
                {formatNumber(stats.pending.bookings)}
              </span>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        {/* <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">إجراءات سريعة</h3>
          <div className="grid grid-cols-2 gap-4">
            <button className="p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-primary-400 hover:bg-primary-50 transition-colors group">
              <CalendarIcon className="w-8 h-8 text-gray-400 group-hover:text-primary-600 mx-auto mb-2" />
              <p className="text-sm font-medium text-gray-600 group-hover:text-primary-700">حجز جديد</p>
            </button>
            <button className="p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-primary-400 hover:bg-primary-50 transition-colors group">
              <TruckIcon className="w-8 h-8 text-gray-400 group-hover:text-primary-600 mx-auto mb-2" />
              <p className="text-sm font-medium text-gray-600 group-hover:text-primary-700">رحلة جديدة</p>
            </button>
            <button className="p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-primary-400 hover:bg-primary-50 transition-colors group">
              <CurrencyDollarIcon className="w-8 h-8 text-gray-400 group-hover:text-primary-600 mx-auto mb-2" />
              <p className="text-sm font-medium text-gray-600 group-hover:text-primary-700">فاتورة جديدة</p>
            </button>
            <button className="p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-primary-400 hover:bg-primary-50 transition-colors group">
              <UsersIcon className="w-8 h-8 text-gray-400 group-hover:text-primary-600 mx-auto mb-2" />
              <p className="text-sm font-medium text-gray-600 group-hover:text-primary-700">عرض التقارير</p>
            </button>
          </div>
        </div> */}
      </div>
    </div>
  );
};

export default Dashboard; 